import React, { createContext, useContext, useEffect, useState } from "react";

export interface User {
  name: string;
  email: string;
  gender: string;
  salutation: string;
  title: string;
  street: string;
  postal_code: string;
  city: string;
  phone: string;
  stripe_customer_id?: string; // wichtig für Stripe-Flow
  is_staff?: boolean;
  is_admin?: boolean;
  is_admin_user?: boolean;
  subscription?: {
    stripe_subscription_id: string;
    plan_name: string;
    status: string;
    current_period_start: string;
    current_period_end: string;
    billing_interval: string;
    cancel_at_period_end: boolean;
    cancel_requested_at: string | null;
    canceled_at: string | null;
    cancellation_policy: string;
    start_date: string;
    price_display?: string;
    status_display?: string;
    currency?: string;
    cancellation_pending?: boolean;
    is_virtual_fallback?: boolean;
    message?: string;
    display_status?: string;
    pending_plan?: {
      name: string;
      tier?: string;
      interval?: string;
    } | null;
    pending_change_at?: string | null;
    stripe_schedule_id?: string | null;
    plan_tier?: string | null;
    is_trial_plan?: boolean;
    can_view_plans?: boolean;
    can_subscribe?: boolean;
  };
}

interface UserContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  refreshUser: (options?: { fresh?: boolean }) => void;
  updateUser: (updates: Partial<User>) => Promise<void>;
}

const UserContext = createContext<UserContextType>({
  user: null,
  loading: true,
  error: null,
  refreshUser: () => { },
  updateUser: async () => { },
});

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const retryTimerRef = React.useRef<number | null>(null);

  const fetchUser = (options?: { fresh?: boolean }) => {
    setLoading(true);
    setError(null);
    const query = options?.fresh ? `?fresh=1&ts=${Date.now()}` : "";
    fetch(`/auth/user/user-profile/${query}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      cache: "no-store",
    })
      .then(async (res) => {
        if (!res.ok) {
          const text = await res.text();
          console.error("Fehlerhafte Antwort:", text);
          setError(`Benutzerprofil konnte nicht geladen werden (Status ${res.status}).`);
          return;
        }
        const data = await res.json();

        
        const safe: User = {
          name: data.name || "",
          email: data.email || "",
          gender: data.gender || "",
          salutation: data.salutation || "",
          title: data.title || "",
          street: data.street || "",
          postal_code: data.postal_code || "",
          city: data.city || "",
          phone: data.phone || "",
          stripe_customer_id: data.stripe_customer_id || "", // ACHTUNG: falls dein API das zurückgibt
          is_admin_user: data.is_admin_user || false,
          is_staff: data.is_staff || false,
          subscription: data.subscription ? {
            stripe_subscription_id: data.subscription.stripe_subscription_id,
            plan_name: data.subscription.plan_name,
            status: data.subscription.status,
            current_period_start: data.subscription.current_period_start,
            current_period_end: data.subscription.current_period_end,
            billing_interval: data.subscription.billing_interval,
            price_display: data.subscription.price_display,
            cancel_at_period_end: data.subscription.cancel_at_period_end,
            cancel_requested_at: data.subscription.cancel_requested_at,
            canceled_at: data.subscription.canceled_at,
            cancellation_policy: data.subscription.cancellation_policy,
            status_display: data.subscription.status_display,
            currency: data.subscription.currency,
            start_date: data.subscription.start_date,
            cancellation_pending: data.subscription.cancellation_pending,
            is_virtual_fallback: data.subscription.is_virtual_fallback,
            message: data.subscription.message,
            display_status: data.subscription.display_status,
            pending_plan: data.subscription.pending_plan ?? null,
            pending_change_at: data.subscription.pending_change_at ?? null,
            stripe_schedule_id: data.subscription.stripe_schedule_id ?? null,
            plan_tier: data.subscription.plan_tier ?? null,
            is_trial_plan: data.subscription.is_trial_plan ?? false,
            can_view_plans: data.subscription.can_view_plans ?? true,
            can_subscribe: data.subscription.can_subscribe ?? true,
          } : undefined,
        };
        setUser(safe);
        setError(null);
      })
      .catch((err) => {
        console.error("Profil-Request fehlgeschlagen:", err);
        setError("Benutzerprofil konnte nicht geladen werden.");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!error || user) return;
    if (retryTimerRef.current) {
      window.clearTimeout(retryTimerRef.current);
    }
    retryTimerRef.current = window.setTimeout(() => {
      fetchUser();
    }, 2000);
    return () => {
      if (retryTimerRef.current) {
        window.clearTimeout(retryTimerRef.current);
      }
    };
  }, [error, user]);

  const updateUser = async (updates: Partial<User>) => {
    await fetch("/auth/user/user-profile/", {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      body: JSON.stringify(updates),
    });
    await fetchUser({ fresh: true });
  };

  useEffect(() => {
    fetchUser();
  }, []);

  return (
    <UserContext.Provider value={{ user, loading, error, refreshUser: fetchUser, updateUser }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => useContext(UserContext);
